// Live agent-progress panel. Rendered as a numbered vertical timeline —
// one step per turn, with tool calls nested underneath each turn. Left
// border + accent dots carry the rhythm; monospace for tool input previews.
//
// Design: the research doc calls this the "Agent Plan" pattern. Replaces
// the flat-pill list treatment that made long runs feel like a log dump.
// Single-accent discipline: only the in-flight turn's dot is accent-blue;
// everything else is text-tone (success/fg-subtle/danger).

import { useMemo } from "react";
import { LoaderCircle, Wrench } from "lucide-react";
import type { AgentEvent } from "../lib/types";
import { cn } from "../lib/cn";

interface Props {
  events: AgentEvent[];
  running: boolean;
  lastText?: string;
  /** Whether the user opted into progressive discovery for this build.
   *  Rendered as a third pill (on/off) next to the classifier chips so
   *  the activity panel reflects the same toggle shown above the form. */
  progressiveDiscovery?: boolean;
}

function briefCategoryLabel(cat: string): string {
  switch (cat) {
    case "open": return "Open-ended";
    case "archetype": return "Archetype-specific";
    case "budget": return "Budget";
    case "mechanic": return "Mechanic-specific";
    case "contrarian": return "Contrarian";
    default: return cat;
  }
}

type ToolResultEv = Extract<AgentEvent, { type: "tool_result" }>;
type TurnEndEv = Extract<AgentEvent, { type: "turn_end" }>;

interface ToolStep {
  callId: string;
  name: string;
  input: unknown;
  result?: ToolResultEv;
}

interface TurnStep {
  turn: number;
  status: "active" | "done" | "error";
  end?: TurnEndEv;
  tools: ToolStep[];
}

/** Group events by turn. Tools live under the turn they fire in;
 *  tool_results match back to their tool_call via tool_use_id. */
function groupByTurn(events: AgentEvent[], running: boolean): TurnStep[] {
  const byTurn = new Map<number, TurnStep>();

  for (const e of events) {
    if (e.type === "turn_start") {
      if (!byTurn.has(e.turn)) {
        byTurn.set(e.turn, { turn: e.turn, status: "active", tools: [] });
      }
    } else if (e.type === "turn_end") {
      const step = byTurn.get(e.turn);
      if (step) {
        step.end = e;
        step.status = "done";
      }
    } else if (e.type === "tool_call") {
      let step = byTurn.get(e.turn);
      if (!step) {
        step = { turn: e.turn, status: "active", tools: [] };
        byTurn.set(e.turn, step);
      }
      step.tools.push({
        callId: e.tool_use_id,
        name: e.name,
        input: e.input,
      });
    } else if (e.type === "tool_result") {
      const step = byTurn.get(e.turn);
      const tool = step?.tools.find((t) => t.callId === e.tool_use_id);
      if (tool) tool.result = e;
      if (step && !e.ok) step.status = "error";
    } else if (e.type === "user_response") {
      // Answers to `ask_user` never produce a tool_result event —
      // the build parks on awaiting_input, then POST /answer appends
      // user_response and the resume path synthesizes a toolResult
      // for Bedrock directly. The UI still needs to close the ask_user
      // row though, so synthesize a tool_result here from the answer.
      for (const step of byTurn.values()) {
        const tool = step.tools.find((t) => t.callId === e.tool_use_id);
        if (tool) {
          const preview = e.answer.length > 120 ? `${e.answer.slice(0, 120)}…` : e.answer;
          tool.result = {
            type: "tool_result",
            turn: step.turn,
            tool_use_id: e.tool_use_id,
            ok: true,
            preview,
            result: e.answer,
          };
          break;
        }
      }
    }
  }

  const sorted = [...byTurn.values()].sort((a, b) => a.turn - b.turn);
  // The latest turn is "active" only while the run is in flight.
  if (!running && sorted.length > 0) {
    const last = sorted[sorted.length - 1]!;
    if (last.status === "active") last.status = "done";
  }
  return sorted;
}

function toolActivity(name: string): { title: string; detail: string } {
  switch (name) {
    case "search_cards": return { title: "Searching for the right cards", detail: "Exploring candidates that fit your deck's plan." };
    case "get_card": return { title: "Taking a closer look", detail: "Checking card details before making a choice." };
    case "get_archetype_exemplars":
    case "get_meta_snapshot": return { title: "Studying the field", detail: "Looking at established decks and the metagame." };
    case "validate_deck":
    case "validate_limited_deck": return { title: "Checking the deck", detail: "Verifying the list against deckbuilding requirements." };
    case "lookup_rule": return { title: "Checking an interaction", detail: "Looking up the rules behind the game plan." };
    case "ask_user": return { title: "Preparing a question for you", detail: "Finding the detail that will help shape your deck." };
    default: return { title: "Working on your deck", detail: "Gathering the next piece of the game plan." };
  }
}

export default function StreamingOutput({ events, running, lastText, progressiveDiscovery }: Props) {
  const classifier = events.find((e) => e.type === "classifier");
  const classifierErr = events.find((e) => e.type === "classifier_error");
  const imageContext = events.find((e) => e.type === "image_context") as
    | Extract<AgentEvent, { type: "image_context" }>
    | undefined;
  const imageContextErr = events.find((e) => e.type === "image_context_error") as
    | Extract<AgentEvent, { type: "image_context_error" }>
    | undefined;
  const errorEvt = events.find((e) => e.type === "error") as
    | Extract<AgentEvent, { type: "error" }>
    | undefined;
  const turns = useMemo(() => groupByTurn(events, running), [events, running]);
  const toolCallCount = turns.reduce((n, t) => n + t.tools.length, 0);
  const inputEvent = [...events].reverse().find((e) => e.type === "awaiting_input" || e.type === "user_response");
  const waiting = running && inputEvent?.type === "awaiting_input";
  const working = running && !waiting && !errorEvt;
  const latestTurn = turns[turns.length - 1];
  const pendingTool = latestTurn?.tools.find((tool) => !tool.result);
  const completedTools = turns.reduce((n, t) => n + t.tools.filter((tool) => tool.result?.ok).length, 0);
  const activity = waiting
    ? { title: "Your turn to shape the deck", detail: "Answer the question above to continue building." }
    : events.some((e) => e.type === "final_answer")
      ? { title: "Saving your deck", detail: "Finishing up your deck and its analysis." }
      : pendingTool
        ? toolActivity(pendingTool.name)
        : lastText
          ? { title: "Developing your deck strategy", detail: "Working through the card choices and game plan." }
          : latestTurn
            ? { title: "Considering the next move", detail: "Weighing card choices, synergies, and the overall game plan." }
            : { title: "Reading your brief", detail: "Getting your deck build started." };

  return (
    <section className="surface build-activity p-5 md:p-6 space-y-5">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="flex items-baseline gap-3">
          <div className="eyebrow">Agent activity</div>
          {running && (
            <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-accent">
              <span className="relative flex h-1.5 w-1.5">
                <span className={cn("absolute inline-flex h-full w-full rounded-full bg-accent opacity-60", working && "build-dot-ping")} />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
              </span>
              {waiting ? "waiting" : working ? "running" : "stopped"}
            </span>
          )}
        </div>
        <div className="font-mono text-[11px] tabular-nums text-fg-subtle">
          {turns.length} pass{turns.length === 1 ? "" : "es"} · {toolCallCount} tool call{toolCallCount === 1 ? "" : "s"}
        </div>
      </header>

      {running && !errorEvt && (
        <div className={cn("build-live rounded-lg border border-accent/20 bg-accent/5 px-4 py-4 flex items-center gap-5 overflow-hidden", working && "build-live--working")}>
          <div className="build-cards shrink-0" aria-hidden="true">
            <span className="build-card" /><span className="build-card" /><span className="build-card" />
          </div>
          <div className="min-w-0 flex-1">
            <div role="status" aria-live="polite" aria-atomic="true" className="flex items-center gap-2 text-sm font-medium text-fg">
              <span key={activity.title} className="build-status-enter">{activity.title}</span>
              {working && <span aria-hidden="true" className="build-thinking flex gap-1"><i /><i /><i /></span>}
            </div>
            <p className="mt-1 text-xs leading-relaxed text-fg-muted">{activity.detail}</p>
            <div className="mt-2 font-mono text-[10px] text-fg-subtle tabular-nums">
              {completedTools > 0 ? `${completedTools} tool call${completedTools === 1 ? "" : "s"} completed` : latestTurn ? "Build in progress" : "Preparing the first pass"}
              {latestTurn && <> · pass {latestTurn.turn}</>}
            </div>
          </div>
        </div>
      )}

      {/* Setup pills — classifier output + the user's per-build toggles.
          One-shot decisions, not steps, so they stay compact pills. */}
      {(classifier || classifierErr || progressiveDiscovery !== undefined) && (
        <div className="flex flex-wrap gap-2">
          {classifier && classifier.type === "classifier" && (
            <>
              <Pill tone="accent">
                brief: {briefCategoryLabel(classifier.category)}
              </Pill>
              <Pill tone={classifier.exemplars_gated ? "warn" : "default"}>
                exemplars: {classifier.exemplars_gated ? "gated off" : "enabled"}
              </Pill>
            </>
          )}
          {classifierErr && classifierErr.type === "classifier_error" && (
            <Pill tone="warn">classifier failed — defaulted to exemplars on</Pill>
          )}
          {progressiveDiscovery !== undefined && (
            <Pill tone={progressiveDiscovery ? "default" : "warn"}>
              questions: {progressiveDiscovery ? "enabled" : "disabled"}
            </Pill>
          )}
        </div>
      )}

      {/* What the vision pre-pass extracted from the user's images.
          Visible BEFORE the timeline so the user can sanity-check
          extraction quality before the agent commits to a deck. */}
      {imageContext && (
        <div className="rounded-md border border-border bg-bg-raised/40 p-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <div className="eyebrow text-[10px]">
              Read from {imageContext.image_count} image
              {imageContext.image_count === 1 ? "" : "s"}
            </div>
            <div className="font-mono text-[10px] text-fg-subtle">
              {imageContext.model.replace(/^us\.anthropic\./, "")}
            </div>
          </div>
          <pre className="whitespace-pre-wrap break-words font-sans text-xs text-fg-muted leading-relaxed max-h-48 overflow-y-auto">
            {imageContext.extracted_text || "(no usable context extracted)"}
          </pre>
        </div>
      )}
      {imageContextErr && (
        <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-xs text-warn">
          image extraction failed — proceeding with text only: {imageContextErr.error}
        </div>
      )}

      {/* Vertical numbered timeline. Empty until the first turn starts. */}
      {turns.length > 0 && (
        <ol className="relative border-l border-border/60 ml-3">
          {turns.map((step) => (
            <TurnStep key={step.turn} step={step} working={working} waiting={waiting} />
          ))}
        </ol>
      )}

      {errorEvt && (
        <div className="text-sm text-danger font-mono">
          error: {errorEvt.error}
        </div>
      )}

      {/* Live streaming-text buffer. Stays put during runs so the user has
          a "something is happening" signal even between tool calls. */}
      {lastText && (
        <div className="border-t border-border pt-4">
          <div className="eyebrow mb-2">Latest reasoning</div>
          <pre className="whitespace-pre-wrap break-words font-sans text-sm text-fg-muted leading-relaxed max-h-56 overflow-y-auto">
            {lastText}
          </pre>
        </div>
      )}
    </section>
  );
}

function TurnStep({ step, working, waiting }: { step: TurnStep; working: boolean; waiting: boolean }) {
  const tone =
    working && (step.status === "active" || step.tools.some((tool) => !tool.result)) ? "active" :
    step.status === "error" ? "error" : "done";

  const dotClass = {
    active: "bg-accent ring-bg-surface",
    done: "bg-fg-subtle ring-bg-surface",
    error: "bg-danger ring-bg-surface",
  }[tone];

  const durationMs = step.end?.tokens ? null : null; // we only get tokens, not elapsed

  return (
    <li className="build-step-enter relative pl-6 pb-6 last:pb-0">
      <span
        aria-hidden
        className={cn(
          "absolute -left-[5px] top-1.5 block h-2.5 w-2.5 rounded-full ring-4",
          dotClass,
          working && tone === "active" && "build-active-dot",
        )}
      />
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[11px] tabular-nums text-fg-subtle">
            {String(step.turn).padStart(2, "0")}
          </span>
          <span className={cn(
            "font-medium text-sm",
            tone === "active" && "text-fg",
            tone === "done" && "text-fg-muted",
            tone === "error" && "text-danger",
          )}>
            Pass {step.turn}
            {step.end?.stop_reason && (
              <span className="ml-2 font-mono text-[11px] text-fg-subtle">
                {step.end.stop_reason}
              </span>
            )}
          </span>
        </div>
        <div className="font-mono text-[11px] tabular-nums text-fg-subtle shrink-0">
          {step.end?.tokens != null && <>{step.end.tokens}t</>}
          {durationMs != null && <> · {(durationMs / 1000).toFixed(1)}s</>}
        </div>
      </div>

      {step.tools.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {step.tools.map((t) => (
            <ToolRow key={t.callId} tool={t} working={working} waiting={waiting} />
          ))}
        </ul>
      )}
    </li>
  );
}

function ToolRow({ tool, working, waiting }: { tool: ToolStep; working: boolean; waiting: boolean }) {
  const inputPreview = useMemo(() => {
    try {
      const s = JSON.stringify(tool.input);
      return s.length > 88 ? `${s.slice(0, 88)}…` : s;
    } catch {
      return "";
    }
  }, [tool.input]);

  const inputFull = useMemo(() => {
    try {
      return JSON.stringify(tool.input, null, 2);
    } catch {
      return String(tool.input);
    }
  }, [tool.input]);

  const Icon = !tool.result && working ? LoaderCircle : Wrench;
  const statusTone: "pending" | "success" | "error" =
    !tool.result ? "pending" :
    tool.result.ok ? "success" : "error";

  return (
    <li className="build-step-enter font-mono text-[12px] leading-relaxed">
      <details className="group">
        {/* Summary row — same visual as before. Clicking it toggles the
            expanded body below. list-none removes the browser's default
            marker; we render our own disclosure chevron. */}
        <summary className="flex items-baseline gap-2 cursor-pointer list-none hover:bg-bg-raised/40 -mx-2 px-2 py-0.5 rounded transition-colors">
          <span
            aria-hidden
            className="text-fg-subtle text-[10px] shrink-0 transition-transform group-open:rotate-90 mt-[2px]"
          >
            ▸
          </span>
          <Icon
            size={11}
            strokeWidth={2}
            className={cn(
              "mt-[2px] shrink-0",
              statusTone === "success" && "text-fg-muted",
              statusTone === "error" && "text-danger",
              statusTone === "pending" && "text-accent",
              !tool.result && working && "build-spinner",
            )}
          />
          <span
            className={cn(
              "font-medium",
              statusTone === "error" && "text-danger",
              statusTone !== "error" && "text-fg",
            )}
          >
            {tool.name}
          </span>
          <span className="text-fg-subtle truncate">{inputPreview}</span>
          {tool.result && (
            <span
              className={cn(
                "ml-auto text-[11px] tabular-nums shrink-0",
                tool.result.ok ? "text-fg-subtle" : "text-danger",
              )}
            >
              {tool.result.ok ? "ok" : "error"}
              {tool.result.preview && tool.result.ok && (
                <span className="ml-2 text-fg-subtle/70 font-sans normal-case">
                  · {tool.result.preview.slice(0, 60)}
                </span>
              )}
            </span>
          )}
          {!tool.result && (
            <span className="ml-auto text-[11px] text-accent shrink-0">
              {waiting ? "waiting" : working ? "running" : "stopped"}
            </span>
          )}
        </summary>

        {/* Expanded body — full JSON input + full result preview. */}
        <div className="mt-1.5 ml-5 space-y-2 text-[11px] leading-relaxed">
          <div>
            <div className="eyebrow mb-1">Request</div>
            <pre className="rounded-md border border-border bg-bg-base/60 p-2.5 text-fg-muted whitespace-pre-wrap break-words overflow-x-auto max-h-64">
              {inputFull}
            </pre>
          </div>
          {tool.result && (tool.result.result || tool.result.preview) && (() => {
            // Prefer the full `result` (up to ~20KB from the backend).
            // Fall back to `preview` for older events that predate the
            // full-payload field. Pretty-print JSON where possible so
            // deep nested tool responses stay readable at a glance.
            const raw = tool.result.result ?? tool.result.preview ?? "";
            const pretty = (() => {
              try {
                return JSON.stringify(JSON.parse(raw), null, 2);
              } catch {
                return raw;
              }
            })();
            return (
              <div>
                <div className="eyebrow mb-1">
                  Response
                  <span
                    className={cn(
                      "ml-2 normal-case tracking-normal",
                      tool.result!.ok ? "text-fg-subtle" : "text-danger",
                    )}
                  >
                    {tool.result!.ok ? "ok" : "error"}
                  </span>
                  <span className="ml-2 normal-case tracking-normal text-fg-subtle/70 font-sans">
                    {raw.length.toLocaleString()} chars
                  </span>
                </div>
                <pre
                  className={cn(
                    "rounded-md border p-2.5 whitespace-pre-wrap break-words overflow-auto max-h-[70vh]",
                    tool.result!.ok
                      ? "border-border bg-bg-base/60 text-fg-muted"
                      : "border-danger/40 bg-danger/5 text-danger",
                  )}
                >
                  {pretty}
                </pre>
              </div>
            );
          })()}
          {tool.result && !tool.result.result && !tool.result.preview && (
            <div className="text-fg-subtle italic">
              (no response body captured)
            </div>
          )}
          {!tool.result && (
            <div className="text-accent italic">
              {waiting ? "Waiting for your answer." : working ? "Waiting for response…" : "No response received."}
            </div>
          )}
        </div>
      </details>
    </li>
  );
}

function Pill({
  tone = "default",
  children,
}: {
  tone?: "default" | "success" | "warn" | "accent";
  children: React.ReactNode;
}) {
  const palette = {
    default: "bg-bg-raised border-border text-fg-muted",
    success: "bg-success/10 border-success/40 text-success",
    warn: "bg-warn/10 border-warn/40 text-warn",
    accent: "bg-accent/10 border-accent/40 text-accent",
  } as const;
  return (
    <span className={cn("badge border font-mono text-[11px]", palette[tone])}>
      {children}
    </span>
  );
}
